import { LegalDoc, LegalDocSet } from '../legal-doc.model';

/**
 * Product-specific terms for Chroniq — an ANNEX to the group-wide AGB, not a second
 * contract (see screendeck-terms.content.ts for the reasoning). Everything
 * cross-cutting stays in the base text.
 *
 * A 5 is the contractual counterpart of section 19 of the Chroniq privacy policy:
 * the policy tells employees the employer has undertaken not to use the reporting
 * functions for continuous behavioural monitoring — that undertaking has to exist
 * somewhere, and this is where it does.
 */

const DE: LegalDoc = {
    title: 'Produktbedingungen Chroniq',
    meta: 'Anhang zu den Allgemeinen Geschäftsbedingungen · Stand: 4. August 2026',
    tocHeading: 'Inhalt',
    footer: 'Massgeblich ist die deutsche Fassung. Übersetzungen dienen der Verständlichkeit.',
    sections: [
        {
            num: 'A 1',
            title: 'Gegenstand und Rangfolge',
            body: `
        <p>Diese Produktbedingungen ergänzen die Allgemeinen Geschäftsbedingungen der Gastro Management
        Group Oberbillig (nachfolgend «AGB») für das Produkt <strong>Chroniq</strong>.</p>
        <p><strong>Vertragspartnerin</strong> der Anbieterin ist der Betrieb als Arbeitgeber (nachfolgend
        «Kundin»). Mitarbeitende nutzen Chroniq im Rahmen ihres Arbeitsverhältnisses; zwischen ihnen und
        der Anbieterin kommt kein eigener entgeltlicher Vertrag zustande. Für sie gelten A 6 sowie die
        Datenschutzerklärung Chroniq.</p>
        <p><strong>Rangfolge:</strong> Im Übrigen gelten die AGB unverändert, insbesondere Vergütung
        (§ 8 AGB), Laufzeit und Kündigung (§ 9 AGB), Verfügbarkeit (§ 10 AGB), Haftung (§ 15 AGB) sowie
        anwendbares Recht und Gerichtsstand (§ 23 AGB). Widersprechen sich diese Produktbedingungen und die
        AGB, gehen diese Produktbedingungen für Chroniq vor. Eine individuelle schriftliche Vereinbarung
        geht beiden vor.</p>
      `,
        },
        {
            num: 'A 2',
            title: 'Leistungsumfang Chroniq',
            body: `
        <p>Chroniq ist eine Plattform zur Zeiterfassung und Personalverwaltung. Je nach den für die Kundin
        freigeschalteten Funktionen umfasst sie:</p>
        <ul>
          <li>Erfassung, Verwaltung und Auswertung von Arbeitszeiten über die App und, sofern eingerichtet,
              über RFID-Badge-Terminals</li>
          <li>Verwaltung von Pausen, Einsätzen und Überstunden</li>
          <li>Schichtplanung einschliesslich Kalenderexport (iCal)</li>
          <li>Abwesenheitsverwaltung und Ferienkonto</li>
          <li>Mitarbeitenden- und Einladungsverwaltung</li>
          <li>Team-Chat, Dateianhänge und betriebsweite Ankündigungen</li>
          <li>rollenbasierten Zugriff (Mitarbeitende, Vorgesetzte, Administration)</li>
          <li>Echtzeitmeldungen und Push-Benachrichtigungen auf iOS-Geräten</li>
          <li>optionale Apple-Wallet-Integration für Mitarbeitendenausweise</li>
        </ul>
        <p>Chroniq unterstützt die Kundin bei der Einhaltung ihrer Aufzeichnungspflichten nach Art. 46 des
        Arbeitsgesetzes und Art. 73 der Verordnung 1 zum Arbeitsgesetz. Die Verantwortung für die
        Richtigkeit und Vollständigkeit der Aufzeichnungen sowie für die Einhaltung des Arbeits- und
        Sozialversicherungsrechts verbleibt bei der Kundin; Chroniq ersetzt keine Rechtsberatung.</p>
      `,
        },
        {
            num: 'A 3',
            title: 'Zugang und Benutzerkonten',
            body: `
        <p>Der Zugang erfolgt ausschliesslich über eine Einladung der Kundin. Eingeladene Personen erhalten
        eine Einladungs-E-Mail und melden sich anschliessend über den Authentifizierungsdienst an. Jedes
        Konto ist persönlich und nicht übertragbar; die gemeinsame Nutzung eines Kontos durch mehrere
        Personen ist unzulässig, weil sie die Zeiterfassung unbrauchbar macht.</p>
        <p>Die Kundin richtet die Konten korrekt ein, hält die Rollenzuweisung aktuell und deaktiviert
        Konten ausscheidender Mitarbeitender unverzüglich. Ein Benutzerkonto endet automatisch mit dem Ende
        des Arbeitsverhältnisses, mit Widerruf der Einladung durch die Kundin oder mit dem Ende des
        Kundenvertrags.</p>
      `,
        },
        {
            num: 'A 4',
            title: 'Pflichten der Kundin als Verantwortliche',
            body: `
        <p>Für die in Chroniq bearbeiteten Mitarbeitendendaten ist die Kundin verantwortliche Stelle; die
        Anbieterin handelt als Auftragsbearbeiterin (§ 11 AGB, Ziff. 01 der Datenschutzerklärung Chroniq).
        Die Kundin verpflichtet sich insbesondere:</p>
        <ul>
          <li>ihre Mitarbeitenden über die Bearbeitung zu informieren (Art. 328b OR, Art. 19 DSG,
              Art. 13 f. DSGVO); die Datenschutzerklärung Chroniq kann dafür verwendet werden;</li>
          <li>nur Daten zu erfassen, die die Eignung für das Arbeitsverhältnis betreffen oder zur
              Durchführung des Arbeitsvertrags erforderlich sind (Art. 328b OR);</li>
          <li>besonders schützenswerte Personendaten, insbesondere Gesundheitsangaben, nicht in
              Freitextfelder einzutragen, soweit dies nicht ausdrücklich erforderlich ist;</li>
          <li>die arbeits-, sozialversicherungs- und gesamtarbeitsvertraglichen Vorgaben einzuhalten,
              namentlich zu Höchstarbeitszeiten, Ruhezeiten und Nacht- und Sonntagsarbeit;</li>
          <li>allfällige Mitwirkungsrechte einer Arbeitnehmervertretung zu beachten.</li>
        </ul>
        <p>Die Freistellung nach § 16 AGB gilt für Verletzungen dieser Pflichten uneingeschränkt.</p>
      `,
        },
        {
            num: 'A 5',
            title: 'Keine Verhaltens- und Leistungskontrolle',
            body: `
        <p>Nach Art. 26 Abs. 1 der Verordnung 3 zum Arbeitsgesetz dürfen Überwachungs- und Kontrollsysteme,
        die das Verhalten der Arbeitnehmenden am Arbeitsplatz überwachen sollen, nicht eingesetzt werden.
        Chroniq ist für die Erfassung der Arbeitszeit und die Organisation der Einsätze bestimmt.</p>
        <p><strong>Die Kundin verpflichtet sich, Chroniq und dessen Auswertungsfunktionen nicht zu einer
        permanenten Überwachung des Verhaltens oder der Leistung einzelner Mitarbeitender zu
        verwenden.</strong> Insbesondere wertet sie Chat-Inhalte, Lesebestätigungen und Anmeldezeitpunkte
        nicht systematisch zu diesem Zweck aus. Zulässig bleiben die Prüfung der Richtigkeit einzelner
        Zeiterfassungen, die Einsatzplanung und die Auswertung aggregierter Betriebskennzahlen.</p>
        <p>Die Anbieterin ist berechtigt, eine Funktion einzuschränken oder zu deaktivieren, wenn konkrete
        Anhaltspunkte für eine Nutzung entgegen dieser Bestimmung bestehen.</p>
      `,
        },
        {
            num: 'A 6',
            title: 'Pflichten der Mitarbeitenden',
            body: `
        <p>Mitarbeitende, die Chroniq nutzen, verpflichten sich:</p>
        <ul>
          <li>Chroniq ausschliesslich für betriebliche Zwecke im Rahmen des Arbeitsverhältnisses zu nutzen;</li>
          <li>Arbeitszeiten wahrheitsgetreu und vollständig zu erfassen und keine unrichtigen Angaben zu
              machen;</li>
          <li>in Freitextfeldern und im Chat keine besonders schützenswerten Personendaten Dritter,
              insbesondere keine Gesundheitsangaben, einzutragen, soweit dies nicht ausdrücklich
              erforderlich ist;</li>
          <li>Zugangsdaten vertraulich zu behandeln, nicht weiterzugeben und einen vermuteten Missbrauch
              unverzüglich zu melden;</li>
          <li>die Plattform nicht missbräuchlich, rechtswidrig oder in einer den Betrieb beeinträchtigenden
              Weise zu nutzen.</li>
        </ul>
        <p>Arbeitsrechtliche Folgen einer Verletzung dieser Pflichten richten sich ausschliesslich nach dem
        Verhältnis zwischen Mitarbeitenden und Arbeitgeber.</p>
      `,
        },
        {
            num: 'A 7',
            title: 'Vergütung Chroniq',
            body: `
        <p>Es gilt § 8 AGB. Die Vergütung richtet sich nach der mit der Kundin vereinbarten Offerte oder
        Bestellung, in der Regel nach der Zahl der aktiven Mitarbeitendenkonten. Einzelne Mitarbeitende
        schulden der Anbieterin keine eigene Vergütung.</p>
      `,
        },
        {
            num: 'A 8',
            title: 'Keine KI-Funktionen',
            body: `
        <p>Chroniq enthält derzeit keine KI-Systeme im Sinne der Verordnung (EU) 2024/1689. Schichtplanung,
        Überstundenberechnung, Ferienkonto und die Behandlung von Abwesenheitsgesuchen beruhen auf fest
        programmierter, regelbasierter Logik. § 7 AGB ist insoweit gegenstandslos.</p>
        <p>Die Anbieterin führt vor der Einführung einer KI-Funktion eine Einstufung nach der KI-Verordnung
        durch und informiert die Kundin mindestens 30 Tage im Voraus in Textform. KI-Systeme im
        Personalbereich gelten nach Anhang III Ziff. 4 der KI-Verordnung regelmässig als
        Hochrisiko-Systeme; in diesem Fall träfen die Kundin als Betreiberin die Pflichten nach Art. 26,
        insbesondere die Information der Arbeitnehmervertretung und der betroffenen Mitarbeitenden vor der
        Inbetriebnahme (Art. 26 Abs. 7).</p>
      `,
        },
        {
            num: 'A 9',
            title: 'Daten bei Vertragsende',
            body: `
        <p>Es gilt § 12 AGB. Die Kundin kann Zeiterfassungs-, Schicht- und Personaldaten jederzeit
        maschinenlesbar exportieren. Sie ist dafür verantwortlich, die Daten vor Vertragsende zu sichern,
        soweit sie ihren gesetzlichen Aufbewahrungspflichten nach Art. 46 ArG, Art. 73 ArGV 1 und OR
        unterliegen. Nach Ablauf der Übergangsfrist gemäss § 12 AGB löscht die Anbieterin die Daten.</p>
      `,
        },
    ],
};

const EN: LegalDoc = {
    title: 'Chroniq Product Terms',
    meta: 'Annex to the General Terms and Conditions · As at 4 August 2026',
    tocHeading: 'Contents',
    footer: 'The German version prevails. Translations are provided for convenience.',
    sections: [
        {
            num: 'A 1',
            title: 'Subject matter and order of precedence',
            body: `
        <p>These product terms supplement the General Terms and Conditions of Gastro Management Group
        Oberbillig (the "GTC") for the <strong>Chroniq</strong> product.</p>
        <p>The provider's <strong>contracting party</strong> is the business as employer (the "customer").
        Employees use Chroniq within their employment relationship; no separate paid contract arises between
        them and the provider. A 6 and the Chroniq privacy policy apply to them.</p>
        <p><strong>Order of precedence:</strong> the GTC otherwise apply unchanged, in particular
        remuneration (§ 8 GTC), term and termination (§ 9 GTC), availability (§ 10 GTC), liability
        (§ 15 GTC) and governing law and jurisdiction (§ 23 GTC). Where these product terms and the GTC
        conflict, these product terms prevail for Chroniq. An individual written agreement prevails over
        both.</p>
      `,
        },
        {
            num: 'A 2',
            title: 'Scope of the Chroniq service',
            body: `
        <p>Chroniq is a platform for time tracking and staff administration. Depending on the functions
        enabled for the customer, it comprises:</p>
        <ul>
          <li>recording, managing and evaluating working time through the app and, where set up, through
              RFID badge terminals</li>
          <li>management of breaks, work sessions and overtime</li>
          <li>shift planning including calendar export (iCal)</li>
          <li>absence management and holiday account</li>
          <li>employee and invitation management</li>
          <li>team chat, file attachments and business-wide announcements</li>
          <li>role-based access (employees, supervisors, administration)</li>
          <li>real-time notices and push notifications on iOS devices</li>
          <li>optional Apple Wallet integration for staff passes</li>
        </ul>
        <p>Chroniq supports the customer in meeting its record-keeping duties under Art. 46 of the
        Employment Act and Art. 73 of Ordinance 1 to the Employment Act. Responsibility for the accuracy and
        completeness of the records and for compliance with employment and social insurance law remains
        with the customer; Chroniq is no substitute for legal advice.</p>
      `,
        },
        {
            num: 'A 3',
            title: 'Access and user accounts',
            body: `
        <p>Access is granted exclusively by invitation from the customer. Invited people receive an
        invitation email and then sign in through the authentication service. Every account is personal and
        non-transferable; sharing an account between several people is not permitted, as it renders time
        tracking useless.</p>
        <p>The customer sets accounts up correctly, keeps role assignments current and deactivates the
        accounts of departing staff without delay. A user account ends automatically when the employment
        relationship ends, when the customer revokes the invitation, or when the customer contract ends.</p>
      `,
        },
        {
            num: 'A 4',
            title: 'The customer\'s duties as controller',
            body: `
        <p>The customer is the controller for the employee data processed in Chroniq; the provider acts as
        processor (§ 11 GTC, section 01 of the Chroniq privacy policy). The customer undertakes in
        particular:</p>
        <ul>
          <li>to inform its employees about the processing (Art. 328b CO, Art. 19 FADP, Art. 13 f. GDPR);
              the Chroniq privacy policy may be used for this purpose;</li>
          <li>to record only data concerning suitability for the job or necessary for performing the
              employment contract (Art. 328b CO);</li>
          <li>not to enter sensitive personal data, in particular health information, into free-text fields
              unless expressly necessary;</li>
          <li>to comply with employment law, social insurance law and collective agreement requirements, in
              particular on maximum working hours, rest periods and night and Sunday work;</li>
          <li>to observe any participation rights of an employee representation body.</li>
        </ul>
        <p>The indemnity under § 16 GTC applies without restriction to breaches of these duties.</p>
      `,
        },
        {
            num: 'A 5',
            title: 'No behavioural or performance monitoring',
            body: `
        <p>Under Art. 26(1) of Ordinance 3 to the Employment Act, monitoring or control systems intended to
        monitor employee behaviour at the workplace may not be used. Chroniq is intended for recording
        working time and organising assignments.</p>
        <p><strong>The customer undertakes not to use Chroniq or its reporting functions for continuous
        monitoring of the behaviour or performance of individual employees.</strong> In particular, it does
        not systematically evaluate chat content, read receipts or sign-in times for that purpose. Checking
        the accuracy of individual time entries, planning assignments and evaluating aggregated operational
        figures remain permitted.</p>
        <p>The provider is entitled to restrict or disable a function where there are concrete indications
        of use contrary to this provision.</p>
      `,
        },
        {
            num: 'A 6',
            title: 'Employees\' obligations',
            body: `
        <p>Employees using Chroniq undertake:</p>
        <ul>
          <li>to use Chroniq exclusively for business purposes within the employment relationship;</li>
          <li>to record working time truthfully and completely and to make no inaccurate entries;</li>
          <li>not to enter sensitive personal data of third parties, in particular health information, into
              free-text fields or the chat unless expressly necessary;</li>
          <li>to keep credentials confidential, not to pass them on, and to report suspected misuse without
              delay;</li>
          <li>not to use the platform improperly, unlawfully or in a way that impairs operations.</li>
        </ul>
        <p>Employment law consequences of a breach are governed exclusively by the relationship between
        employee and employer.</p>
      `,
        },
        {
            num: 'A 7',
            title: 'Chroniq remuneration',
            body: `
        <p>§ 8 GTC applies. Remuneration follows the quotation or order agreed with the customer, as a rule
        based on the number of active employee accounts. Individual employees owe the provider no
        remuneration of their own.</p>
      `,
        },
        {
            num: 'A 8',
            title: 'No AI functions',
            body: `
        <p>Chroniq currently contains no AI systems within the meaning of Regulation (EU) 2024/1689. Shift
        planning, overtime calculation, the holiday account and the handling of absence requests rest on
        hard-coded, rule-based logic. § 7 GTC is therefore without object here.</p>
        <p>Before introducing an AI function, the provider will classify it under the AI Act and inform the
        customer at least 30 days in advance in text form. AI systems in the employment context regularly
        count as high-risk systems under Annex III point 4 of the AI Act; in that case the customer, as
        deployer, would be subject to the obligations of Art. 26, in particular informing the employee
        representation body and the employees concerned before putting the system into service
        (Art. 26(7)).</p>
      `,
        },
        {
            num: 'A 9',
            title: 'Data on termination',
            body: `
        <p>§ 12 GTC applies. The customer can export time, shift and staff data in machine-readable form at
        any time. It is responsible for securing the data before the contract ends, in so far as it is
        subject to its statutory retention duties under Art. 46 Employment Act, Art. 73 Ordinance 1 to the
        Employment Act and the Code of Obligations. The provider deletes the data once the transition period
        under § 12 GTC has expired.</p>
      `,
        },
    ],
};

const FR: LegalDoc = {
    title: 'Conditions produit Chroniq',
    meta: 'Annexe aux conditions générales · État au 4 août 2026',
    tocHeading: 'Sommaire',
    footer: 'La version allemande fait foi. Les traductions servent à la compréhension.',
    sections: [
        {
            num: 'A 1',
            title: 'Objet et rang',
            body: `
        <p>Les présentes conditions produit complètent les conditions générales de la Gastro Management
        Group Oberbillig (ci-après « CG ») pour le produit <strong>Chroniq</strong>.</p>
        <p>La <strong>partie contractante</strong> de l'exploitante est l'établissement en qualité
        d'employeur (ci-après « le client »). Les collaborateurs utilisent Chroniq dans le cadre de leurs
        rapports de travail ; aucun contrat onéreux distinct ne naît entre eux et l'exploitante. Le
        chiffre A 6 et la déclaration de protection des données Chroniq leur sont applicables.</p>
        <p><strong>Rang :</strong> pour le surplus, les CG s'appliquent sans changement, en particulier la
        rémunération (§ 8 CG), la durée et la résiliation (§ 9 CG), la disponibilité (§ 10 CG), la
        responsabilité (§ 15 CG) ainsi que le droit applicable et le for (§ 23 CG). En cas de contradiction,
        les présentes conditions produit priment pour Chroniq. Une convention écrite individuelle prime les
        deux.</p>
      `,
        },
        {
            num: 'A 2',
            title: 'Étendue des prestations Chroniq',
            body: `
        <p>Chroniq est une plateforme de saisie du temps de travail et de gestion du personnel. Selon les
        fonctions activées pour le client, elle comprend :</p>
        <ul>
          <li>la saisie, la gestion et l'analyse du temps de travail via l'application et, si installés, via
              des terminaux de badge RFID</li>
          <li>la gestion des pauses, des périodes de travail et des heures supplémentaires</li>
          <li>la planification des services, y compris l'export d'agenda (iCal)</li>
          <li>la gestion des absences et le compte de vacances</li>
          <li>la gestion des collaborateurs et des invitations</li>
          <li>la messagerie d'équipe, les pièces jointes et les annonces à l'échelle de l'établissement</li>
          <li>l'accès fondé sur les rôles (collaborateurs, responsables, administration)</li>
          <li>les avis en temps réel et les notifications push sur les appareils iOS</li>
          <li>l'intégration facultative d'Apple Wallet pour les badges collaborateurs</li>
        </ul>
        <p>Chroniq assiste le client dans le respect de ses obligations d'enregistrement selon l'art. 46 de
        la loi sur le travail et l'art. 73 de l'ordonnance 1 relative à la loi sur le travail. La
        responsabilité de l'exactitude et de l'exhaustivité des enregistrements ainsi que du respect du
        droit du travail et des assurances sociales incombe au client ; Chroniq ne remplace pas un conseil
        juridique.</p>
      `,
        },
        {
            num: 'A 3',
            title: 'Accès et comptes utilisateurs',
            body: `
        <p>L'accès s'effectue exclusivement sur invitation du client. Les personnes invitées reçoivent un
        courriel d'invitation puis se connectent via le service d'authentification. Chaque compte est
        personnel et incessible ; le partage d'un compte entre plusieurs personnes est interdit, car il
        rend la saisie du temps inutilisable.</p>
        <p>Le client configure correctement les comptes, tient à jour l'attribution des rôles et désactive
        sans délai les comptes des collaborateurs sortants. Un compte prend automatiquement fin avec la fin
        des rapports de travail, avec la révocation de l'invitation par le client ou avec la fin du contrat
        client.</p>
      `,
        },
        {
            num: 'A 4',
            title: 'Obligations du client en tant que responsable du traitement',
            body: `
        <p>Pour les données des collaborateurs traitées dans Chroniq, le client est responsable du
        traitement ; l'exploitante agit comme sous-traitante (§ 11 CG, ch. 01 de la déclaration de
        protection des données Chroniq). Le client s'engage notamment :</p>
        <ul>
          <li>à informer ses collaborateurs du traitement (art. 328b CO, art. 19 LPD, art. 13 s. RGPD) ; la
              déclaration de protection des données Chroniq peut être utilisée à cette fin ;</li>
          <li>à ne saisir que les données portant sur les aptitudes du travailleur à remplir son emploi ou
              nécessaires à l'exécution du contrat de travail (art. 328b CO) ;</li>
          <li>à ne pas inscrire de données sensibles, en particulier des indications de santé, dans les
              champs de texte libre, sauf nécessité expresse ;</li>
          <li>à respecter les prescriptions du droit du travail, des assurances sociales et des conventions
              collectives, notamment sur la durée maximale du travail, les temps de repos ainsi que le
              travail de nuit et du dimanche ;</li>
          <li>à respecter les éventuels droits de participation d'une représentation des travailleurs.</li>
        </ul>
        <p>La garantie prévue au § 16 CG s'applique sans restriction aux violations de ces obligations.</p>
      `,
        },
        {
            num: 'A 5',
            title: 'Pas de contrôle du comportement ou des performances',
            body: `
        <p>Selon l'art. 26 al. 1 de l'ordonnance 3 relative à la loi sur le travail, les systèmes de
        surveillance ou de contrôle destinés à surveiller le comportement des travailleurs à leur poste de
        travail ne peuvent pas être utilisés. Chroniq est destiné à l'enregistrement du temps de travail et
        à l'organisation des engagements.</p>
        <p><strong>Le client s'engage à ne pas utiliser Chroniq ni ses fonctions d'analyse pour une
        surveillance permanente du comportement ou des performances de collaborateurs
        déterminés.</strong> En particulier, il n'exploite pas systématiquement à cette fin le contenu des
        messages, les accusés de lecture ou les heures de connexion. Restent admis la vérification de
        l'exactitude de saisies isolées, la planification des engagements et l'analyse d'indicateurs
        agrégés.</p>
        <p>L'exploitante est en droit de restreindre ou de désactiver une fonction en présence d'indices
        concrets d'une utilisation contraire à la présente disposition.</p>
      `,
        },
        {
            num: 'A 6',
            title: 'Obligations des collaborateurs',
            body: `
        <p>Les collaborateurs qui utilisent Chroniq s'engagent :</p>
        <ul>
          <li>à utiliser Chroniq exclusivement à des fins professionnelles dans le cadre des rapports de
              travail ;</li>
          <li>à saisir le temps de travail de manière véridique et complète et à ne faire aucune indication
              inexacte ;</li>
          <li>à ne pas inscrire dans les champs de texte libre ni dans la messagerie de données sensibles
              de tiers, en particulier des indications de santé, sauf nécessité expresse ;</li>
          <li>à traiter leurs identifiants de manière confidentielle, à ne pas les transmettre et à
              signaler sans délai tout abus présumé ;</li>
          <li>à ne pas utiliser la plateforme de manière abusive, illicite ou de nature à perturber
              l'exploitation.</li>
        </ul>
        <p>Les conséquences de droit du travail d'une violation relèvent exclusivement de la relation entre
        le collaborateur et l'employeur.</p>
      `,
        },
        {
            num: 'A 7',
            title: 'Rémunération Chroniq',
            body: `
        <p>Le § 8 CG s'applique. La rémunération suit l'offre ou la commande convenue avec le client, en
        règle générale selon le nombre de comptes collaborateurs actifs. Les collaborateurs pris
        individuellement ne doivent aucune rémunération propre à l'exploitante.</p>
      `,
        },
        {
            num: 'A 8',
            title: 'Absence de fonctions d\'IA',
            body: `
        <p>Chroniq ne comporte actuellement aucun système d'IA au sens du règlement (UE) 2024/1689. La
        planification des services, le calcul des heures supplémentaires, le compte de vacances et le
        traitement des demandes d'absence reposent sur une logique programmée fondée sur des règles. Le
        § 7 CG est dès lors sans objet.</p>
        <p>Avant d'introduire une fonction d'IA, l'exploitante procède à sa classification selon le
        règlement sur l'IA et en informe le client au moins 30 jours à l'avance sous forme de texte. Les
        systèmes d'IA dans le domaine des ressources humaines sont régulièrement considérés comme des
        systèmes à haut risque selon l'annexe III ch. 4 du règlement ; dans ce cas, le client devrait, en
        tant que déployeur, remplir les obligations de l'art. 26, en particulier informer la représentation
        des travailleurs et les collaborateurs concernés avant la mise en service (art. 26 par. 7).</p>
      `,
        },
        {
            num: 'A 9',
            title: 'Données à la fin du contrat',
            body: `
        <p>Le § 12 CG s'applique. Le client peut exporter en tout temps les données de temps, de service et
        de personnel dans un format lisible par machine. Il lui incombe de sauvegarder les données avant la
        fin du contrat, dans la mesure où elles sont soumises à ses obligations légales de conservation
        selon l'art. 46 LTr, l'art. 73 OLT 1 et le CO. À l'expiration du délai transitoire prévu au
        § 12 CG, l'exploitante efface les données.</p>
      `,
        },
    ],
};

const IT: LegalDoc = {
    title: 'Condizioni di prodotto Chroniq',
    meta: 'Allegato alle condizioni generali · Stato: 4 agosto 2026',
    tocHeading: 'Indice',
    footer: 'Fa fede la versione tedesca. Le traduzioni servono alla comprensione.',
    sections: [
        {
            num: 'A 1',
            title: 'Oggetto e rango',
            body: `
        <p>Le presenti condizioni di prodotto integrano le condizioni generali della Gastro Management
        Group Oberbillig (di seguito «CG») per il prodotto <strong>Chroniq</strong>.</p>
        <p>La <strong>controparte contrattuale</strong> della gerente è l'azienda in qualità di datore di
        lavoro (di seguito «il cliente»). I collaboratori utilizzano Chroniq nell'ambito del proprio
        rapporto di lavoro; tra loro e la gerente non sorge alcun contratto oneroso autonomo. Ad essi si
        applicano il n. A 6 e l'informativa sulla protezione dei dati Chroniq.</p>
        <p><strong>Rango:</strong> per il resto valgono immutate le CG, in particolare la remunerazione
        (§ 8 CG), la durata e la disdetta (§ 9 CG), la disponibilità (§ 10 CG), la responsabilità
        (§ 15 CG) nonché il diritto applicabile e il foro (§ 23 CG). In caso di contraddizione, le presenti
        condizioni di prodotto prevalgono per Chroniq. Un accordo scritto individuale prevale su entrambi.</p>
      `,
        },
        {
            num: 'A 2',
            title: 'Portata delle prestazioni Chroniq',
            body: `
        <p>Chroniq è una piattaforma per la rilevazione delle ore e la gestione del personale. A seconda
        delle funzioni attivate per il cliente comprende:</p>
        <ul>
          <li>rilevazione, gestione e analisi delle ore di lavoro tramite l'app e, se installati, tramite
              terminali badge RFID</li>
          <li>gestione di pause, periodi di lavoro e ore supplementari</li>
          <li>pianificazione dei turni, compresa l'esportazione del calendario (iCal)</li>
          <li>gestione delle assenze e conto vacanze</li>
          <li>gestione dei collaboratori e degli inviti</li>
          <li>chat di squadra, allegati e comunicazioni a livello aziendale</li>
          <li>accesso basato sui ruoli (collaboratori, responsabili, amministrazione)</li>
          <li>avvisi in tempo reale e notifiche push sui dispositivi iOS</li>
          <li>integrazione facoltativa di Apple Wallet per le tessere dei collaboratori</li>
        </ul>
        <p>Chroniq assiste il cliente nell'adempimento dei suoi obblighi di registrazione secondo l'art. 46
        della legge sul lavoro e l'art. 73 dell'ordinanza 1 concernente la legge sul lavoro. La
        responsabilità per l'esattezza e la completezza delle registrazioni nonché per il rispetto del
        diritto del lavoro e delle assicurazioni sociali resta al cliente; Chroniq non sostituisce una
        consulenza giuridica.</p>
      `,
        },
        {
            num: 'A 3',
            title: 'Accesso e account utente',
            body: `
        <p>L'accesso avviene esclusivamente su invito del cliente. Le persone invitate ricevono un'e-mail
        di invito e si autenticano quindi tramite il servizio di autenticazione. Ogni account è personale e
        non trasferibile; la condivisione di un account tra più persone non è ammessa, poiché rende
        inutilizzabile la rilevazione delle ore.</p>
        <p>Il cliente configura correttamente gli account, mantiene aggiornata l'attribuzione dei ruoli e
        disattiva senza indugio gli account dei collaboratori uscenti. Un account termina automaticamente
        con la fine del rapporto di lavoro, con la revoca dell'invito da parte del cliente o con la fine del
        contratto.</p>
      `,
        },
        {
            num: 'A 4',
            title: 'Obblighi del cliente quale titolare del trattamento',
            body: `
        <p>Per i dati dei collaboratori trattati in Chroniq il cliente è titolare del trattamento; la
        gerente agisce quale responsabile del trattamento (§ 11 CG, n. 01 dell'informativa Chroniq). Il
        cliente si impegna in particolare:</p>
        <ul>
          <li>a informare i propri collaboratori del trattamento (art. 328b CO, art. 19 LPD,
              art. 13 seg. RGPD); a tal fine può essere utilizzata l'informativa Chroniq;</li>
          <li>a rilevare unicamente dati che riguardano l'idoneità al rapporto di lavoro o che sono
              necessari all'esecuzione del contratto di lavoro (art. 328b CO);</li>
          <li>a non inserire dati degni di particolare protezione, in particolare indicazioni sulla salute,
              nei campi di testo libero, salvo espressa necessità;</li>
          <li>a rispettare le prescrizioni del diritto del lavoro, delle assicurazioni sociali e dei
              contratti collettivi, segnatamente su durata massima del lavoro, tempi di riposo e lavoro
              notturno e domenicale;</li>
          <li>a rispettare gli eventuali diritti di partecipazione di una rappresentanza dei lavoratori.</li>
        </ul>
        <p>La manleva secondo il § 16 CG si applica senza restrizioni alle violazioni di tali obblighi.</p>
      `,
        },
        {
            num: 'A 5',
            title: 'Nessun controllo del comportamento o delle prestazioni',
            body: `
        <p>Secondo l'art. 26 cpv. 1 dell'ordinanza 3 concernente la legge sul lavoro, i sistemi di
        sorveglianza o di controllo destinati a sorvegliare il comportamento dei lavoratori sul posto di
        lavoro non possono essere impiegati. Chroniq è destinato alla rilevazione del tempo di lavoro e
        all'organizzazione degli impieghi.</p>
        <p><strong>Il cliente si impegna a non utilizzare Chroniq né le sue funzioni di analisi per una
        sorveglianza permanente del comportamento o delle prestazioni di singoli collaboratori.</strong> In
        particolare non valuta sistematicamente a tale scopo i contenuti della chat, le conferme di lettura
        o gli orari di accesso. Restano ammessi la verifica dell'esattezza di singole rilevazioni, la
        pianificazione degli impieghi e l'analisi di indicatori aziendali aggregati.</p>
        <p>La gerente è legittimata a limitare o disattivare una funzione in presenza di indizi concreti di
        un utilizzo contrario alla presente disposizione.</p>
      `,
        },
        {
            num: 'A 6',
            title: 'Obblighi dei collaboratori',
            body: `
        <p>I collaboratori che utilizzano Chroniq si impegnano:</p>
        <ul>
          <li>a utilizzare Chroniq esclusivamente per scopi aziendali nell'ambito del rapporto di lavoro;</li>
          <li>a rilevare le ore di lavoro in modo veritiero e completo e a non fornire indicazioni
              inesatte;</li>
          <li>a non inserire nei campi di testo libero e nella chat dati di terzi degni di particolare
              protezione, in particolare indicazioni sulla salute, salvo espressa necessità;</li>
          <li>a trattare le credenziali in modo riservato, a non trasmetterle e a segnalare senza indugio
              un abuso presunto;</li>
          <li>a non utilizzare la piattaforma in modo abusivo, illecito o tale da pregiudicare
              l'esercizio.</li>
        </ul>
        <p>Le conseguenze di diritto del lavoro di una violazione sono rette esclusivamente dal rapporto
        tra collaboratore e datore di lavoro.</p>
      `,
        },
        {
            num: 'A 7',
            title: 'Remunerazione Chroniq',
            body: `
        <p>Si applica il § 8 CG. La remunerazione segue l'offerta o l'ordine convenuti con il cliente, di
        regola in funzione del numero di account collaboratore attivi. I singoli collaboratori non devono
        alla gerente alcuna remunerazione propria.</p>
      `,
        },
        {
            num: 'A 8',
            title: 'Assenza di funzioni di IA',
            body: `
        <p>Chroniq non contiene attualmente sistemi di IA ai sensi del regolamento (UE) 2024/1689. La
        pianificazione dei turni, il calcolo delle ore supplementari, il conto vacanze e la gestione delle
        richieste di assenza si fondano su una logica programmata basata su regole. Il § 7 CG è pertanto
        privo di oggetto.</p>
        <p>Prima di introdurre una funzione di IA la gerente ne effettua la classificazione secondo il
        regolamento sull'IA e ne informa il cliente almeno 30 giorni prima in forma testuale. I sistemi di
        IA nell'ambito del personale sono di regola considerati sistemi ad alto rischio secondo l'allegato
        III n. 4 del regolamento; in tal caso il cliente, quale deployer, sarebbe soggetto agli obblighi
        dell'art. 26, in particolare all'informazione della rappresentanza dei lavoratori e dei
        collaboratori interessati prima della messa in servizio (art. 26 par. 7).</p>
      `,
        },
        {
            num: 'A 9',
            title: 'Dati al termine del contratto',
            body: `
        <p>Si applica il § 12 CG. Il cliente può esportare in ogni momento i dati di rilevazione, di turno e
        del personale in formato leggibile da macchina. Gli incombe salvaguardare i dati prima della fine
        del contratto, nella misura in cui siano soggetti ai suoi obblighi legali di conservazione secondo
        l'art. 46 LL, l'art. 73 OLL 1 e il CO. Scaduto il termine transitorio di cui al § 12 CG la gerente
        cancella i dati.</p>
      `,
        },
    ],
};

export const CHRONIQ_TERMS: LegalDocSet = { de: DE, en: EN, fr: FR, it: IT };
